import { memo } from 'react';
import type { StaffRating } from '../../types/staff';
import { Star } from 'lucide-react';
import { isStrongRating, ratingTone } from '../../utils/staffProfile';
import { cn } from '../../utils/cn';

interface StaffRatingBadgeProps {
  rating: StaffRating | string;
  className?: string;
}

/**
 * Pill for a behavioural signal rating (strong / solid / mixed / weak / unknown).
 * `strong` is rare and sits above `solid`, so it swaps the dot for a filled star.
 */
export const StaffRatingBadge = memo(({ rating, className }: StaffRatingBadgeProps) => {
  const strong = isStrongRating(rating);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border capitalize',
        ratingTone(rating),
        strong && 'ring-1 ring-purple-400/30 shadow-[0_0_0_3px_rgba(168,85,247,0.08)]',
        className,
      )}
      title={strong ? 'Strong: the rare top rating, above solid' : undefined}
    >
      {strong ? (
        <Star className="w-3 h-3 fill-current" aria-hidden="true" />
      ) : (
        <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
      )}
      {rating || 'unknown'}
    </span>
  );
});

StaffRatingBadge.displayName = 'StaffRatingBadge';
